import type { AuthProviderId } from "./authProviders";
import type { B2cTokens } from "./b2cClient";

/**
 * Side-panel sign-in hand-off (password-manager autofill fix).
 *
 * Chrome's built-in password manager refuses chrome-extension:// pages and the
 * third-party managers are unreliable inside a cross-origin iframe, so the
 * form in the extension's side-panel iframe never gets fill/save. Instead the
 * iframe opens a TOP-LEVEL popup of this same app (route `/ext-signin`), where
 * password managers work, and the popup hands the resulting B2C tokens back to
 * the iframe with `window.opener.postMessage`.
 *
 * The iframe and the popup are SAME-ORIGIN, so every check here is against
 * `window.location.origin` — no extension id or manifest is involved. The
 * message is not storage, so Chrome's storage partitioning (the iframe's
 * localStorage is partitioned under the extension origin) does not matter.
 */

export const EXT_SIGNIN_PATH = "/ext-signin";
export const EXT_SIGNIN_MESSAGE_TYPE = "M360_EXT_SIGNIN_SESSION";

/** The only provider the popup signs into (the primary "Atlas account" form). */
export const EXT_SIGNIN_PROVIDER: AuthProviderId = "provider-portal-b2c";

export function newNonce(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function buildPopupUrl(origin: string, nonce: string): string {
  const u = new URL(EXT_SIGNIN_PATH, origin);
  u.searchParams.set("nonce", nonce);
  return u.toString();
}

export interface HandoffSession {
  providerId: AuthProviderId;
  tokens: B2cTokens;
}

export interface HandoffExpectation {
  origin: string;
  source: unknown;
  nonce: string;
}

function isTokens(v: unknown): v is B2cTokens {
  if (!v || typeof v !== "object") return false;
  const t = v as Record<string, unknown>;
  return (
    typeof t.access_token === "string" &&
    t.access_token.length > 0 &&
    typeof t.expires_at === "number" &&
    typeof t.token_type === "string" &&
    (t.refresh_token === undefined || typeof t.refresh_token === "string")
  );
}

/**
 * Returns the session iff the message is from OUR popup (same origin, exact
 * window, one-time nonce) and well-formed; null for everything else.
 */
export function acceptHandoff(
  event: { origin: string; source: unknown; data: unknown },
  expected: HandoffExpectation,
): HandoffSession | null {
  if (event.origin !== expected.origin) return null;
  if (!expected.source || event.source !== expected.source) return null;
  const d = event.data as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return null;
  if (d.type !== EXT_SIGNIN_MESSAGE_TYPE) return null;
  if (typeof d.nonce !== "string" || d.nonce !== expected.nonce) return null;
  if (d.providerId !== EXT_SIGNIN_PROVIDER) return null;
  if (!isTokens(d.tokens)) return null;
  return { providerId: EXT_SIGNIN_PROVIDER, tokens: d.tokens };
}

/** Popup side. `origin` is always the popup's own origin — never "*". */
export function postSessionToOpener(
  opener: Window,
  nonce: string,
  providerId: AuthProviderId,
  tokens: B2cTokens,
  origin: string,
): void {
  opener.postMessage(
    { type: EXT_SIGNIN_MESSAGE_TYPE, nonce, providerId, tokens },
    origin,
  );
}
